'use client';

import {
  createContext,
  useCallback,
  useContext,
  useState,
  type ReactNode,
} from 'react';
import { setByPath as setByPathFn } from './set-by-path';

export type Values = Record<string, unknown>;

export type RichTextMark = 'bold' | 'italic' | 'underline';
export type RichTextListKind = 'bullet' | 'numbered';
export type RichTextFont =
  | 'inherit'
  | 'display'
  | 'sans'
  | 'serif'
  | 'mono'
  | 'cormorant'
  | 'libre'
  | 'greatVibes'
  | 'inter'
  | 'georgia'
  | 'times'
  | 'bricolage'
  | 'geist'
  | 'newsreader'
  | 'jetbrains';
export type RichTextAlign = 'left' | 'center' | 'right';

export type RichTextSegment = {
  text: string;
  marks?: RichTextMark[];
  link?: string;
  list?: RichTextListKind;
};

export type RichTextValue = {
  type: 'richText';
  content: RichTextSegment[];
  style?: {
    fontFamily?: RichTextFont;
    fontSize?: number;
    textAlign?: RichTextAlign;
    color?: string;
  };
};

export type ImageValue = {
  storageId?: string;
  url?: string | null;
  alt?: string;
  positionX?: number;
  positionY?: number;
};

export type VideoValue = {
  storageId?: string;
  url?: string | null;
  mimeType?: string;
};

export type ImagePickerProps = {
  path: string;
  mode?: 'inline' | 'library';
  currentValue: ImageValue | undefined;
  draftValue?: ImageValue | undefined;
  onDraftChange?: (next: ImageValue | undefined) => void;
  onPersist?: (next: ImageValue | undefined) => void;
  onCommit?: (next: ImageValue | undefined) => void;
  onRemove?: () => void;
  onClose: () => void;
};

export type VideoPickerProps = {
  path: string;
  currentValue: VideoValue | undefined;
  onClose: () => void;
};

export type EditModeContextValue = {
  editing: boolean;
  values: Values;
  setByPath: (path: string, value: unknown) => void;
  selectedPath: string | null;
  setSelected: (path: string | null) => void;
  renderImagePicker?: (props: ImagePickerProps) => ReactNode;
  renderVideoPicker?: (props: VideoPickerProps) => ReactNode;
};

const EditModeContext = createContext<EditModeContextValue>({
  editing: false,
  values: {},
  setByPath: () => {},
  selectedPath: null,
  setSelected: () => {},
});

export function useEditMode(): EditModeContextValue {
  return useContext(EditModeContext);
}

type ProviderProps = {
  editing: boolean;
  values: Values;
  onChange: (next: Values) => void;
  initialSelectedPath?: string | null;
  renderImagePicker?: (props: ImagePickerProps) => ReactNode;
  renderVideoPicker?: (props: VideoPickerProps) => ReactNode;
  children: ReactNode;
};

export function EditModeProvider({
  editing,
  values,
  onChange,
  initialSelectedPath = null,
  renderImagePicker,
  renderVideoPicker,
  children,
}: ProviderProps) {
  const [selectedPath, setSelected] = useState<string | null>(initialSelectedPath);

  const setByPath = useCallback(
    (path: string, value: unknown) => {
      onChange(setByPathFn(values, path, value));
    },
    [values, onChange],
  );

  return (
    <EditModeContext.Provider
      value={{
        editing,
        values,
        setByPath,
        selectedPath,
        setSelected,
        renderImagePicker,
        renderVideoPicker,
      }}
    >
      {children}
    </EditModeContext.Provider>
  );
}
